/**
 * WHAT CHART ANALYTICA DRAWS on the chart it audited.
 *
 * <pre>
 *   a SCOPE band       bar 0 to the clicked bar, faint, so the range scanned is visible
 *   a CLICK vertical   the bar the scan stopped at, solid and labelled
 *   AUDIT verticals    dashed, through the bars carrying a scored event
 *   EVENT markers      a mark per analytical event, with its callout and hover card
 * </pre>
 *
 * <h2>Three overlays, not one, and each is separately switchable</h2>
 *
 * The master annotation toggle hides the markers and the audit verticals and KEEPS the scope
 * band and the click line. Those two say which bars were scanned, which is the one thing a
 * reader still needs when they have turned the annotations off to see the price action — hiding
 * them too would leave a dashboard describing a range nothing on the chart identifies.
 *
 * <h2>CAPS ARE REPORTED, never silent</h2>
 *
 * A 33-year chart can carry several hundred CTS points and several thousand boundary events.
 * ECharts lays out every markLine datum on every redraw, so they are capped — and the cap is
 * returned alongside the overlays so the dashboard can print "showing 200 of 1,412". A silent
 * truncation reads as "this is everything", which is the one thing an audit must never imply.
 */

import { CHART_ANALYTICA_COLOR, CHART_ANALYTICA_UP, CHART_ANALYTICA_DOWN } from './analyticaColors';
import { fixed, pct } from './analyticaStats';

export const ANALYTICA_SCOPE_ID = 'chart-analytica-scope';
export const ANALYTICA_AUDIT_ID = 'chart-analytica-audit';
export const ANALYTICA_MARKERS_ID = 'chart-analytica-markers';

/** Every id this module can produce. What a caller sweeps to clear a previous run. */
export const ANALYTICA_OVERLAY_IDS = [
  ANALYTICA_SCOPE_ID, ANALYTICA_AUDIT_ID, ANALYTICA_MARKERS_ID,
];

/**
 * Ceilings, and the reason they differ.
 *
 * <p>Markers are scatter points in one series and are cheap; verticals are markLine data that
 * ECharts positions individually on every redraw and are not. Both are small enough that a
 * reader can still see the price action underneath, which is the real constraint rather than the
 * frame rate.
 */
export const MAX_MARKERS = 240;
export const MAX_AUDIT_LINES = 120;

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The most RECENT `max` entries BY BAR, which is the half of a long scan a reader is looking at.
 *
 * <p>Keeping the earliest would truncate the chart's right-hand end — exactly where the clicked
 * bar is, and exactly the stretch the reader asked about. The CTS classification request already
 * made this change for the same reason, having first shipped the other way round.
 *
 * <p><b>It SORTS first, and that is not defensive.</b> The rows arrive in the order the CTS
 * overlay listed its points, which happens to be bar order today because `reversalBars` walks
 * the dataset forwards — an accidental property, not a guarantee. Slicing the tail of an
 * unsorted list keeps whichever marks happened to be listed last, so the cap would silently
 * drop recent bars and keep old ones with nothing to show for it. Caught by a fixture whose
 * points wrapped.
 */
function recent(list, max) {
  const sorted = [...list].sort((a, b) => a.index - b.index);
  return sorted.length <= max ? sorted : sorted.slice(sorted.length - max);
}

/**
 * Builds the overlays for one report.
 *
 * @param {object} report the output of `runChartAnalytica`
 * @param {Array<string>} dates the chart's axis
 * @param {boolean} annotations whether the markers and audit lines are shown
 * @returns {{overlays:Array<object>, markerCount:number, markerTotal:number,
 *            auditCount:number, auditTotal:number}}
 */
export function analyticaOverlays(report, dates, annotations = true) {
  const empty = { overlays: [], markerCount: 0, markerTotal: 0, auditCount: 0, auditTotal: 0 };
  if (!report || !report.ok) return empty;
  const axis = Array.isArray(dates) ? dates : [];
  const to = n(report.upToIndex);
  if (to === null || axis.length === 0) return empty;

  const overlays = [];
  const from = axis[0];
  const clicked = axis[Math.min(to, axis.length - 1)];

  // ── the scope, which survives the annotation toggle ──────────────────────────────────────
  if (from && clicked) {
    overlays.push({
      type: 'analyticaScope',
      id: ANALYTICA_SCOPE_ID,
      label: 'Chart Analytica scope',
      date1: from,
      date2: clicked,
      clickedDate: clicked,
      color: CHART_ANALYTICA_COLOR,
    });
  }

  if (!annotations) return { ...empty, overlays };

  // ── the markers ──────────────────────────────────────────────────────────────────────────
  const scored = (report.edge?.rows || report.cts?.rows || []);
  const markerTotal = scored.length;
  const points = recent(scored, MAX_MARKERS)
    .map(row => markerFor(row, axis))
    .filter(Boolean);

  if (points.length > 0) {
    overlays.push({
      type: 'pivotMarkers',
      id: ANALYTICA_MARKERS_ID,
      label: 'Chart Analytica',
      symbol: 'diamond',
      symbolSize: 11,
      symbolOffsetPx: 22,
      insideLabelFontSize: 9,
      labelFontSize: 10,
      color: CHART_ANALYTICA_COLOR,
      points,
    });
  }

  // ── the audit verticals, through the bars that carry a marker ────────────────────────────
  //
  // The SAME bars, deliberately: a vertical through a bar with no mark on it is a line a reader
  // cannot account for, and two different selections would make the chart look like two studies.
  const auditRows = recent(scored, MAX_AUDIT_LINES);
  const lines = auditRows
    .map(r => ({ date: axis[r.index], direction: r.direction }))
    .filter(l => typeof l.date === 'string');

  if (lines.length > 0) {
    overlays.push({
      type: 'analyticaAudit',
      id: ANALYTICA_AUDIT_ID,
      label: 'Chart Analytica audit',
      lines,
      color: CHART_ANALYTICA_COLOR,
    });
  }

  return {
    overlays,
    markerCount: points.length,
    markerTotal,
    auditCount: lines.length,
    auditTotal: markerTotal,
  };
}

/**
 * One CTS point, as a mark that SAYS what the analysis found at it.
 *
 * <p>Direction decides the colour and the side, the score decides the opacity, and the zone is
 * the one-character label inside the diamond — so the three things a reader scans for are
 * readable without hovering, and the rest is in the card.
 *
 * <p>An UNRESOLVED point (one whose horizon runs past the clicked bar) is drawn HOLLOW. It is
 * not a failure and must not look like one: the same convention the celestial study uses for a
 * snapped date, and the chart's existing answer to "this mark is provisional".
 */
function markerFor(row, axis) {
  const date = axis[row.index];
  const price = n(row.close);
  if (typeof date !== 'string' || price === null) return null;

  const up = row.direction === 'up';
  const score = n(row.score);
  const zoneDigit = row.zone ? row.zone.slice(-1) : null;

  const lines = [
    `Direction      ${up ? 'UPSIDE reversal' : 'DOWNSIDE reversal'}`,
    `Zone           ${row.zone || 'no channel in force'}`
      + (row.category ? `  (${row.category})` : ''),
    `From equator   ${row.distance === null ? 'n/a' : `${fixed(row.distance)} band heights`}`,
    `RSI            ${row.rsi === null ? 'n/a' : fixed(row.rsi, 1)}`,
    `Divergence     ${row.divergence
      ? `${row.divergence}${row.divergenceAgrees ? ' (agrees)' : ' (conflicts)'}`
      : 'none within the window'}`,
    `Rel. volume    ${row.rvol === null || row.rvol === undefined ? 'n/a' : `${fixed(row.rvol)}x`}`,
    score === null ? null : `Edge score     ${score} / 100`,
    row.resolved
      ? `Outcome        ${row.success ? 'SUCCESS' : 'failed'}`
        + `  ${row.returnPct === null ? '' : `(${fixed(row.returnPct)}% over the horizon)`}`
      : 'Outcome        UNRESOLVED — the horizon runs past the scanned range',
  ].filter(Boolean);

  return {
    date,
    price,
    position: up ? 'below' : 'above',
    ...(up ? {} : { symbolRotate: 180 }),
    color: up ? CHART_ANALYTICA_UP : CHART_ANALYTICA_DOWN,
    // Hollow for a provisional mark. `opacity` is the channel the overlay renderer offers, and
    // halving it is what this chart already means by "not a result yet".
    ...(row.resolved ? {} : { opacity: 0.45 }),
    ...(zoneDigit ? { label: zoneDigit, labelInside: true } : {}),
    title: `${date} — ${up ? 'CTS up' : 'CTS down'}`
      + (score === null ? '' : `, edge ${score}`),
    tooltipLines: lines,
  };
}

/**
 * The summary line the status bar prints after a run — including what was NOT drawn.
 *
 * <p>Separate from the dashboard because the dashboard may be closed: a reader who ran the scan
 * from the context menu and left the panel shut still has to be told it ran, how long it took,
 * and that the chart is showing a subset.
 */
export function analyticaStatus(report, drawn) {
  if (!report || !report.ok) return report?.reason || 'Chart Analytica could not run.';
  const parts = [
    `Chart Analytica: ${report.scannedBars.toLocaleString()} bars`
      + ` to ${report.clickedDate}`,
    `${report.inputs.bands} section${report.inputs.bands === 1 ? '' : 's'}`,
    `${report.inputs.ctsPoints} CTS point${report.inputs.ctsPoints === 1 ? '' : 's'}`,
  ];
  if (drawn && drawn.markerTotal > drawn.markerCount) {
    parts.push(`showing the most recent ${drawn.markerCount} of ${drawn.markerTotal} marks`);
  }
  const edge = report.edge?.separation;
  if (n(edge) !== null) parts.push(`high-vs-low edge separation ${pct(edge)}`);
  parts.push(`${Math.round(report.elapsedMs)} ms`);
  return `${parts.join(' · ')}.`;
}

export default analyticaOverlays;
